/**
 * Authentication Error Types
 *
 * Centralized constants for all authentication-related error types.
 * These constants are used throughout the authentication flow to ensure
 * type safety and eliminate magic strings.
 */

/**
 * Authentication error types that can occur during login, registration, or session management
 */
export const AUTH_ERROR_TYPES = {
  /** User needs to enroll in MFA (Multi-Factor Authentication) */
  MFA_ENROLL_REQUIRED: 'MFA_ENROLL_REQUIRED',

  /** User has MFA enrolled but needs to verify their identity */
  MFA_VERIFICATION_REQUIRED: 'MFA_VERIFICATION_REQUIRED',

  /** Account is locked by Okta due to security policies */
  OKTA_ACCOUNT_LOCKED: 'OKTA_ACCOUNT_LOCKED',

  /** Account is temporarily locked due to failed password attempts */
  ACCOUNT_TEMPORARILY_LOCKED: 'ACCOUNT_TEMPORARILY_LOCKED',

  /** User's password has expired and needs to be reset */
  PASSWORD_EXPIRED: 'Your password has expired. Please reset your password to continue.',

  /** Brand mismatch between JWT token and application configuration */
  BRAND_MISMATCH: 'BRAND_MISMATCH',

  /** Access denied due to insufficient permissions */
  ACCESS_DENIED: 'access_denied',

  /** Invalid application scope */
  INVALID_SCOPE: 'invalid_scope',

  /** Authentication server error */
  SERVER_ERROR: 'server_error',
} as const

/**
 * Type for authentication error types
 */
export type AuthErrorType = (typeof AUTH_ERROR_TYPES)[keyof typeof AUTH_ERROR_TYPES]

/**
 * Error types that require MFA enrollment flow
 */
export const MFA_ENROLLMENT_ERRORS = [AUTH_ERROR_TYPES.MFA_ENROLL_REQUIRED] as const

/**
 * Error types that require MFA verification flow
 */
export const MFA_VERIFICATION_ERRORS = [AUTH_ERROR_TYPES.MFA_VERIFICATION_REQUIRED] as const

/**
 * Error types that should preserve their original message when shown to users
 */
export const PRESERVED_ERROR_MESSAGES = [
  AUTH_ERROR_TYPES.MFA_ENROLL_REQUIRED,
  AUTH_ERROR_TYPES.OKTA_ACCOUNT_LOCKED,
  AUTH_ERROR_TYPES.ACCOUNT_TEMPORARILY_LOCKED,
  AUTH_ERROR_TYPES.PASSWORD_EXPIRED,
  AUTH_ERROR_TYPES.BRAND_MISMATCH,
] as const

/**
 * Check if an error type requires MFA enrollment
 */
export const isMfaEnrollmentError = (errorType: string): boolean => MFA_ENROLLMENT_ERRORS.includes(errorType as any)

/**
 * Check if an error type requires MFA verification
 */
export const isMfaVerificationError = (errorType: string): boolean => MFA_VERIFICATION_ERRORS.includes(errorType as any)

/**
 * Check if an error message should be preserved (not replaced with generic message)
 */
export const shouldPreserveErrorMessage = (errorMessage: string): boolean => PRESERVED_ERROR_MESSAGES.includes(errorMessage as any)
